'use client';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';

export default function TrueMedWidget({
  checkout,
  productId,
  eligible,
  className = ''
}) {
  const [widgetAvailable, setWidgetAvailable] = useState(false);
  const [widgetReady, setWidgetReady] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeError, setIframeError] = useState(false);
  const scriptLoaded = useRef(false);
  const truemedIframeSrc =
    'https://app.truemed.com/widget/v8/tm_qual_g4z906ltm3';

  useEffect(() => {
    if (scriptLoaded.current) return;
    if (!eligible) return;
    const script = document.createElement('script');
    script.src =
      'https://static.truemed.com/widgets/product-page-widget.min.js';
    script.defer = true;
    document.body.appendChild(script);

    scriptLoaded.current = true;
  }, [eligible]);

  useEffect(() => {
    // reset states on every product change
    setWidgetAvailable(false);
    setWidgetReady(false);

    // If not eligible, stop right here
    if (!eligible) {
      return;
    }

    const target = document.querySelector('[id^="truemed-instructions"]');
    if (!target) return;

    setWidgetAvailable(true);

    if (target.querySelector('.truemed-entry-text')) {
      setWidgetReady(true);
      return;
    }

    const observer = new MutationObserver(() => {
      if (target.querySelector('.truemed-entry-text')) {
        setWidgetReady(true);
        observer.disconnect();
      }
    });

    observer.observe(target, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [checkout, productId, eligible]);

  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width to avoid page shift
      const scrollbarWidth =
        window.innerWidth - document.documentElement.clientWidth;
      document.body.classList.add('overflow-hidden');
      document.body.style.paddingRight =
        scrollbarWidth > 0 ? `${scrollbarWidth}px` : '';
    } else {
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    }

    // Cleanup when component unmounts
    return () => {
      document.body.classList.remove('overflow-hidden');
      document.body.style.paddingRight = '';
    };
  }, [isOpen]);

  if (!eligible) {
    return null; // cleanly return nothing for ineligible products
  }

  return (
    <div className={`mt-4 ${checkout ? '' : 'pb-6'} ${className}`}>
      <div
        className={`truemed-custom-ui min-h-1 ${checkout ? 'checkout-true' : 'checkout-false'}`}
      >
        <div
          id="truemed-instructions"
          data-public-id="tm_qual_g4z906ltm3"
          data-mode-headless="true"
        ></div>
        {widgetAvailable && widgetReady && (
          <button
            onClick={() => {
              setIsOpen(true);
              setIframeLoaded(false);
              setIframeError(false);
            }}
            className={`truemed-learn-more ${checkout ? 'text-black' : 'text-white'}`}
          >
            Learn more
          </button>
        )}
      </div>
      {isOpen && (
        <div
          className="truemed-modal fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => setIsOpen(false)}
        >
          <div
            className="truemed-modal-content relative w-full max-w-lg rounded-lg bg-white p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              className="truemed-instructions-header mb-4 flex items-center justify-between"
              tabIndex="-1"
            >
              <div>
                <Image
                  src="https://static.truemed.com/widgets/truemed-navy.svg"
                  width={150}
                  height={40}
                  loading="lazy"
                  alt="Truemed logo"
                />
              </div>
              <button
                aria-label="Close Truemed modal"
                className="truemed-instructions-close-button"
                onClick={() => setIsOpen(false)}
              >
                <div>
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    role="img"
                    height="20"
                    width="20"
                  >
                    <g transform="translate(0, 0) scale(1)">
                      <path
                        d="M10.872 10.004c.449-.14.875-.373 1.23-.729l5.602-5.601L16.29 2.26l-5.602 5.601a.999.999 0 01-1.414 0l-5.57-5.57L2.29 3.705l5.57 5.57c.364.364.8.6 1.261.738a2.86 2.86 0 00-1.229.708L2.29 16.322l1.414 1.414 5.603-5.601a1.022 1.022 0 011.413 0l5.57 5.57 1.414-1.414-5.57-5.57a2.869 2.869 0 00-1.262-.717z"
                        fill="rgba(14, 14, 15, 1)"
                      ></path>
                    </g>
                  </svg>
                </div>
              </button>
            </div>

            {/* Loader */}
            {!iframeLoaded && !iframeError && (
              <div className="flex h-64 items-center justify-center">
                <svg
                  className="h-10 w-10 animate-spin text-gray-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              </div>
            )}

            {iframeError && (
              <div className="text-center text-sm">
                Unable to load widget at this time. Please try again.
              </div>
            )}

            {/* Truemed Iframe */}
            <iframe
              src={truemedIframeSrc}
              className={`mt-4 h-100 w-full ${iframeLoaded ? 'block' : 'hidden'}`}
              title="Truemed Widget"
              onLoad={() => setIframeLoaded(true)}
              onError={(e) => {
                console.error('Truemed iframe failed to load:', e);
                setIframeError(true);
                setIsOpen(false);
              }}
            ></iframe>
          </div>
        </div>
      )}
    </div>
  );
}
